import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '@/providers/AuthProvider';
import { toast } from 'sonner';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';

const PROVIDER_LABELS: Record<string, string> = { google: 'Google', github: 'GitHub' };

/**
 * Auth Callback Handler
 *
 * Handles OAuth callbacks from Neon Auth (Better Auth).
 * Shows provider-specific loading, error UI, and redirects to intended destination.
 */
const AuthCallback = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshSession, isAuthenticated, loading } = useAuth();
  const [error, setError] = useState<string | null>(null);

  // Read provider stored before OAuth redirect
  const pendingProvider = localStorage.getItem('pendingOAuthProvider');
  const providerLabel = pendingProvider ? PROVIDER_LABELS[pendingProvider] : null;

  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        // Check for error in URL params
        const urlError = searchParams.get('error');
        if (urlError) {
          throw new Error(urlError);
        }

        // Better Auth handles OAuth callback automatically
        // Refresh session to pick up the authenticated state
        await refreshSession();

        // Small delay to allow session to propagate
        await new Promise(resolve => setTimeout(resolve, 500));

        await refreshSession();
      } catch (err) {
        console.error('Authentication callback error:', err);
        const message = err instanceof Error ? err.message : 'Failed to complete authentication.';
        setError(message);
      }
    };

    handleAuthCallback();
  }, [refreshSession, searchParams]);

  // On successful auth, redirect to intended destination
  useEffect(() => {
    if (!loading && isAuthenticated) {
      const redirectTo = localStorage.getItem('authRedirectTo') || '/canvas';
      // Clean up localStorage
      localStorage.removeItem('pendingOAuthProvider');
      localStorage.removeItem('authRedirectTo');

      toast.success('Welcome!', {
        description: 'Successfully signed in to Platform Engineering Nexus.',
      });
      navigate(redirectTo);
    }
  }, [loading, isAuthenticated, navigate]);

  // Timeout: if not authenticated after 5s, show error
  useEffect(() => {
    if (error) return; // Already showing error

    const timeout = setTimeout(() => {
      if (!loading && !isAuthenticated) {
        setError('Authentication timed out. Please try again.');
      }
    }, 5000);

    return () => clearTimeout(timeout);
  }, [loading, isAuthenticated, error]);

  // Clean up localStorage on error path too
  useEffect(() => {
    if (error) {
      localStorage.removeItem('pendingOAuthProvider');
      localStorage.removeItem('authRedirectTo');
    }
  }, [error]);

  // Error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-neutral-50 via-white to-neutral-100">
        <div className="text-center max-w-md px-4">
          <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">Authentication failed</h2>
          <p className="text-muted-foreground mb-6">{error}</p>
          <div className="flex gap-3 justify-center">
            <Button asChild variant="default">
              <Link to="/auth">Try Again</Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/">Back to Home</Link>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Loading state with provider-specific message
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-neutral-50 via-white to-neutral-100">
      <div className="text-center">
        <LoadingSpinner size="lg" />
        <h2 className="text-lg font-semibold mt-4">
          {providerLabel ? `Signing in with ${providerLabel}...` : 'Processing authentication...'}
        </h2>
        <p className="text-muted-foreground">Please wait while we complete your sign in.</p>
      </div>
    </div>
  );
};

export default AuthCallback;
